import {
  dateTimeToDateTimeRecord,
  jsDateToDateTime,
  DateTimeRecord,
  dateTimeRecordToLocalisedDateTime,
  evaluateStateRanges,
  StateRange,
} from "@visioni/schedules";
import { AlarmSchedule, AlarmFeatureName } from "@visioni/a3s-alarm-schedules";
import { AlarmException, AlarmOverride, AlarmResponse } from "@src/services/api/types";
import { getCurrentCameraArmedState, isCameraArmedByResponse, getScheduleFromAlarmDetail } from "./alarm-utils";
import { isArmedFromArmingType } from "@src/features/schedules/utils/schedule-utils";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";

export type ScheduledTransition = {
  datetime: Date;
  toState: "armed" | "disarmed";
  datetimeRecord: DateTimeRecord;
};

export type FindNextTransitionOptions = {
  alarmDetail: AlarmConfiguration;
  from?: Date;
  maxDays?: number;
};

// Helper: Common setup for transition finding functions
type TransitionSetupResult = {
  schedule: AlarmSchedule;
  response: AlarmResponse;
  fromDateTime: ReturnType<typeof jsDateToDateTime>;
  fromRecord: DateTimeRecord;
  isCurrentlyArmed: boolean;
};

const setupTransitionFinding = (alarmDetail: AlarmConfiguration, from: Date): TransitionSetupResult | null => {
  if (!alarmDetail.settings) {
    return null;
  }

  const schedule = getScheduleFromAlarmDetail(alarmDetail);
  const response = alarmDetail.settings.response;

  if (!schedule || !response) {
    return null;
  }

  const tz = schedule.metadata.timezone;
  const fromDateTime = jsDateToDateTime(from).setZone(tz);
  const fromRecord = dateTimeToDateTimeRecord(fromDateTime);
  const isCurrentlyArmed = isCameraArmedByResponse(schedule, response, fromRecord);

  return {
    schedule,
    response,
    fromDateTime,
    fromRecord,
    isCurrentlyArmed,
  };
};

// Helper: Find transitions using evaluateStateRanges from the schedule package
const findTransitionsUsingStateRanges = (
  setup: TransitionSetupResult,
  maxDays: number,
  shouldReturnTransition: (
    previousIsArmed: boolean,
    currentIsArmed: boolean,
    transitionDateTime: ReturnType<typeof jsDateToDateTime>,
    transitionRecord: DateTimeRecord,
  ) => ScheduledTransition | null,
): ScheduledTransition | null => {
  const { schedule, response, fromDateTime, fromRecord, isCurrentlyArmed } = setup;
  const timezone = schedule.metadata.timezone;
  const endDateTime = fromDateTime.plus({ days: maxDays });
  const endRecord = dateTimeToDateTimeRecord(endDateTime);

  const orderedRecords = Array.from(
    Object.keys(response)
      .filter((feature) => feature in schedule.features)
      .flatMap((feature) =>
        Array.from<StateRange>(
          evaluateStateRanges(schedule, AlarmFeatureName.parse(feature), { start: fromRecord, end: endRecord }),
        ).flatMap((stateRange) => [
          { record: stateRange.start, isEnd: false },
          { record: stateRange.end, isEnd: true },
        ]),
      )
      .reduce((map, { record, isEnd }) => {
        const dt = dateTimeRecordToLocalisedDateTime(record, timezone);
        const inWindow = isEnd ? dt > fromDateTime && dt <= endDateTime : dt >= fromDateTime && dt < endDateTime;
        if (inWindow) map.set(dt.toMillis(), record);
        return map;
      }, new Map<number, DateTimeRecord>()),
  )
    .sort(([a], [b]) => a - b)
    .map(([, record]) => record);

  let previousIsArmed = isCurrentlyArmed;
  for (const record of orderedRecords) {
    const transitionDateTime = dateTimeRecordToLocalisedDateTime(record, timezone);
    const currentIsArmed = isCameraArmedByResponse(schedule, response, record);
    const transition = shouldReturnTransition(previousIsArmed, currentIsArmed, transitionDateTime, record);
    if (transition) return transition;
    previousIsArmed = currentIsArmed;
  }

  return null;
};

// Find next transition. If targetState is provided, only returns transitions to that state, otherwise returns any state change.
export const findNextTransition = (
  options: FindNextTransitionOptions & { targetState?: "armed" | "disarmed" },
): ScheduledTransition | null => {
  const { alarmDetail, targetState, from = new Date(), maxDays = 30 } = options;

  const setup = setupTransitionFinding(alarmDetail, from);
  return setup ?
      findTransitionsUsingStateRanges(
        setup,
        maxDays,
        (previousIsArmed, currentIsArmed, transitionDateTime, transitionRecord) => {
          if (previousIsArmed === currentIsArmed) return null;

          const toState = currentIsArmed ? "armed" : "disarmed";
          if (targetState && toState !== targetState) return null;

          return {
            datetime: transitionDateTime.toJSDate(),
            toState,
            datetimeRecord: transitionRecord,
          };
        },
      )
    : null;
};

// Optimized version of findNextTransition that checks fewer days
export const findNextTransitionOptimized = ({ alarmDetail }: { alarmDetail: AlarmConfiguration }) => {
  // First try with only 3 days for faster computation
  const quickResult = findNextTransition({ alarmDetail, maxDays: 3 });
  if (quickResult) return quickResult;

  // If no transition found in next 3 days, try 7 days
  const weekResult = findNextTransition({ alarmDetail, maxDays: 7 });
  if (weekResult) return weekResult;

  // Only fall back to full 30 days if really necessary
  return findNextTransition({ alarmDetail, maxDays: 30 });
};

// Find next transition to specific state (armed/disarmed)
export const findNextTransitionToState = (
  options: FindNextTransitionOptions & { targetState: "armed" | "disarmed" },
): ScheduledTransition | null => {
  return findNextTransition(options);
};

// Find transition after given transition.
export const findNextTransitionAfter = (
  options: FindNextTransitionOptions & { after: ScheduledTransition },
): ScheduledTransition | null => {
  return findNextTransition({
    ...options,
    from: options.after.datetime,
  });
};

// Helper: create exception with start/end period.
const createExceptionWithPeriod = (
  action: "armed" | "disarmed",
  startDateTime: ReturnType<typeof jsDateToDateTime>,
  endDateTime: ReturnType<typeof jsDateToDateTime>,
  reason: string,
  idPrefix: string,
): AlarmException => {
  return {
    id: `${idPrefix}-${Date.now()}`,
    reason,
    action,
    author: null,
    period: {
      start: dateTimeToDateTimeRecord(startDateTime),
      end: dateTimeToDateTimeRecord(endDateTime),
    },
  };
};

// Helper: create override with period (end can be null for indefinite).
const createOverrideWithPeriod = (
  action: "armed" | "disarmed",
  startDateTime: ReturnType<typeof jsDateToDateTime>,
  endDateTime: ReturnType<typeof jsDateToDateTime> | null,
  reason: string,
  idPrefix: string,
): AlarmOverride => {
  return {
    id: `${idPrefix}-${Date.now()}`,
    reason,
    action,
    author: null,
    period: {
      start: dateTimeToDateTimeRecord(startDateTime),
      end: endDateTime ? dateTimeToDateTimeRecord(endDateTime) : null,
    },
  };
};

// Create exception to keep state longer: starts at next transition to opposite state, extends by durationMinutes.
// e.g. Keep armed for 15m longer: starts at next disarm, extends by durationMinutes.
export const createKeepStateLongerException = (
  schedule: AlarmSchedule,
  nextTransition: ScheduledTransition,
  action: "armed" | "disarmed",
  durationMinutes: number,
  reason: string,
): AlarmException => {
  const timezone = schedule.metadata.timezone;
  const startDateTime = jsDateToDateTime(nextTransition.datetime).setZone(timezone);
  const endDateTime = startDateTime.plus({ minutes: durationMinutes });

  return createExceptionWithPeriod(action, startDateTime, endDateTime, reason, "extend-state-longer");
};

// Create exception to keep state until next scheduled: uses second transition if same state, else defaults to 24h.
const createKeepStateUntilNextScheduledException = (
  schedule: AlarmSchedule,
  firstTransition: ScheduledTransition,
  secondTransition: ScheduledTransition | null,
  targetState: "armed" | "disarmed",
  reason: string,
  idPrefix: string,
): AlarmException => {
  const timezone = schedule.metadata.timezone;
  const startDateTime = jsDateToDateTime(firstTransition.datetime).setZone(timezone);

  const endDateTime =
    secondTransition && secondTransition.toState === targetState ?
      jsDateToDateTime(secondTransition.datetime).setZone(timezone)
    : startDateTime.plus({ hours: 24 });

  return createExceptionWithPeriod(targetState, startDateTime, endDateTime, reason, idPrefix);
};

export const createKeepArmedUntilNextScheduledException = (
  schedule: AlarmSchedule,
  firstTransition: ScheduledTransition,
  secondTransition: ScheduledTransition | null,
  reason: string = "Keep armed until next scheduled",
): AlarmException => {
  return createKeepStateUntilNextScheduledException(
    schedule,
    firstTransition,
    secondTransition,
    "disarmed",
    reason,
    "keep-armed-until-next",
  );
};

export const createKeepDisarmedUntilNextScheduledException = (
  schedule: AlarmSchedule,
  firstTransition: ScheduledTransition,
  secondTransition: ScheduledTransition | null,
  reason: string = "Keep disarmed until next scheduled",
): AlarmException => {
  return createKeepStateUntilNextScheduledException(
    schedule,
    firstTransition,
    secondTransition,
    "armed",
    reason,
    "keep-disarmed-until-next",
  );
};

// Create override to keep state indefinitely: starts at next transition, end is null.
const createKeepStateIndefinitelyOverride = (
  schedule: AlarmSchedule,
  nextTransition: ScheduledTransition,
  action: "armed" | "disarmed",
  reason: string,
  idPrefix: string,
): AlarmOverride => {
  const timezone = schedule.metadata.timezone;
  const startDateTime = jsDateToDateTime(nextTransition.datetime).setZone(timezone);

  return createOverrideWithPeriod(action, startDateTime, null, reason, idPrefix);
};

export const createKeepArmedIndefinitelyOverride = (
  schedule: AlarmSchedule,
  nextTransition: ScheduledTransition,
  reason: string = "Keep armed indefinitely",
): AlarmOverride => {
  return createKeepStateIndefinitelyOverride(schedule, nextTransition, "armed", reason, "keep-armed-indefinitely");
};

export const createKeepDisarmedIndefinitelyOverride = (
  schedule: AlarmSchedule,
  nextTransition: ScheduledTransition,
  reason: string = "Keep disarmed indefinitely",
): AlarmOverride => {
  return createKeepStateIndefinitelyOverride(
    schedule,
    nextTransition,
    "disarmed",
    reason,
    "keep-disarmed-indefinitely",
  );
};

// Create immediate override: starts now, lasts for durationMinutes (or indefinitely if durationMinutes not provided).
const createImmediateStateOverride = (
  schedule: AlarmSchedule,
  action: "armed" | "disarmed",
  durationMinutes: number | undefined,
  reason: string,
  idPrefix: string,
  from: Date = new Date(),
): AlarmOverride => {
  const timezone = schedule.metadata.timezone;
  const startDateTime = jsDateToDateTime(from).setZone(timezone);
  const endDateTime = durationMinutes !== undefined ? startDateTime.plus({ minutes: durationMinutes }) : null;

  return createOverrideWithPeriod(action, startDateTime, endDateTime, reason, idPrefix);
};

export const createImmediateArmOverride = (
  schedule: AlarmSchedule,
  durationMinutes?: number,
  reason: string = "Arm now",
  from: Date = new Date(),
): AlarmOverride => {
  return createImmediateStateOverride(schedule, "armed", durationMinutes, reason, "arm-now", from);
};

export const createImmediateDisarmOverride = (
  schedule: AlarmSchedule,
  durationMinutes?: number,
  reason: string = "Disarm now",
  from: Date = new Date(),
): AlarmOverride => {
  return createImmediateStateOverride(schedule, "disarmed", durationMinutes, reason, "disarm-now", from);
};

// Check which actions are available based on current state and upcoming transitions.
export const availableNextActions = (
  alarmDetail: AlarmConfiguration,
  from: Date = new Date(),
): {
  canKeepArmedLonger: boolean;
  canKeepArmedUntilNextScheduled: boolean;
  canKeepArmedIndefinitely: boolean;
  canResumeSchedule: boolean;
  nextDisarmTransition: ScheduledTransition | null;
  nextArmedTransition: ScheduledTransition | null;
} => {
  if (!alarmDetail.settings) {
    return {
      canKeepArmedLonger: false,
      canKeepArmedUntilNextScheduled: false,
      canKeepArmedIndefinitely: false,
      canResumeSchedule: false,
      nextDisarmTransition: null,
      nextArmedTransition: null,
    };
  }

  const currentState = getCurrentCameraArmedState(alarmDetail, from);
  const isCurrentlyArmed = isArmedFromArmingType(currentState?.armingType);

  const nextDisarm = findNextTransitionToState({
    alarmDetail,
    targetState: "disarmed",
    from,
  });

  const nextArmed = findNextTransitionToState({
    alarmDetail,
    targetState: "armed",
    from,
  });

  const nextTransition = findNextTransition({ alarmDetail, from });
  const canKeepArmedLonger = isCurrentlyArmed && nextDisarm !== null;
  const canKeepArmedUntilNextScheduled = isCurrentlyArmed && nextDisarm !== null;
  const canKeepArmedIndefinitely = nextTransition !== null;
  const canResumeSchedule = currentState?.isInOverride === true;

  return {
    canKeepArmedLonger,
    canKeepArmedUntilNextScheduled,
    canKeepArmedIndefinitely,
    canResumeSchedule,
    nextDisarmTransition: nextDisarm,
    nextArmedTransition: nextArmed,
  };
};

// Can resume if an override is active
export const canResumeSchedule = (alarmDetail: AlarmConfiguration, from: Date = new Date()): boolean => {
  if (!alarmDetail.settings) {
    return false;
  }

  const currentState = getCurrentCameraArmedState(alarmDetail, from);
  return currentState?.isInOverride === true;
};

// Returns the AlarmDetail with override set to null, and the newly computed Schedule.
export const resumeAlarmSchedule = (
  alarmDetail: AlarmConfiguration,
): { alarmDetail: AlarmConfiguration; schedule: AlarmSchedule | null } => {
  const schedule = getScheduleFromAlarmDetail(alarmDetail);

  if (!alarmDetail.settings) {
    return { alarmDetail, schedule };
  }

  const alarmDetailWithoutOverride: AlarmConfiguration = {
    ...alarmDetail,
    settings: {
      ...alarmDetail.settings,
      override: null,
    },
  };

  return { alarmDetail: alarmDetailWithoutOverride, schedule };
};
